import { CredentialsAPI } from "@/API/Credentials"
import { AppDispatch } from "@/redux"
import { appActions } from "@/redux/slice/appSlice"
import { CredentialCreateRequestBody, GoogleSheetTokensResponse } from "@/types/credentials"
import { showNotification } from "./notificationService"
import { CredentialMessage, formattedCredentialMessage } from "@/constants/credentials/credential"
import { GeneralAPI } from "@/API/General"
import { GOOGLE_AUTH_SCOPE_DRIVE } from "@/constants/TCS/TCS"
import { credentialActions } from "@/redux/slice/credentialSlice"

export const getCredentials = (
  token: string,
  limit: number = 10,
  offset: number = 0
) => async (dispatch: AppDispatch) => {
  dispatch(appActions.setLoading(true))
  const resp = await CredentialsAPI.getAll(token, limit, offset)
  if (resp.data) {
    dispatch(credentialActions.setCredentials(resp.data.credentials))
    dispatch(credentialActions.setPagination({
      total: resp.data.total,
      limit,
      offset
    }))
  }
  dispatch(appActions.setLoading(false))
  return resp
}

export const createCredentials = (
  token: string,
  body: CredentialCreateRequestBody
) => async (dispatch: AppDispatch) => {
  dispatch(appActions.setLoading(true))
  const resp = await CredentialsAPI.create(token, body)
  showNotification(
    resp,
    CredentialMessage.CREATE_SUCCESSFUL,
    formattedCredentialMessage
  )
  if (resp.data) {
    await dispatch(getCredentials(token))
  }
  dispatch(appActions.setLoading(false))
  return resp
}

export const deleteCredential = (
  token: string,
  credentialId: string
) => async (dispatch: AppDispatch) => {
  dispatch(appActions.setLoading(true))
  const resp = await CredentialsAPI.delete(token, credentialId)
  showNotification(
    resp,
    CredentialMessage.DELETE_SUCCESSFUL,
    formattedCredentialMessage
  )
  if (resp.data) {
    await dispatch(getCredentials(token))
  }
  dispatch(appActions.setLoading(false))
  return resp
}

export const getCredentialDetails = (
  token: string,
  credentialId: string
) => async (dispatch: AppDispatch) => {
  dispatch(appActions.setLoading(true))
  const resp = await CredentialsAPI.getCredentialDetails(token, credentialId)
  dispatch(appActions.setLoading(false))
  return resp
}

export const updateCredentials = (
  token: string,
  credentialId: string,
  body: CredentialCreateRequestBody
) => async (dispatch: AppDispatch) => {
  dispatch(appActions.setLoading(true))
  const resp = await CredentialsAPI.update(token, credentialId, body)
  showNotification(
    resp,
    CredentialMessage.UPDATE_SUCCESSFUL,
    formattedCredentialMessage
  )
  if (resp.data) {
    await dispatch(getCredentials(token))
  }
  dispatch(appActions.setLoading(false))
  return resp
}

export const getGoogleTokensFromAuthCode = (
  token: string,
  authCode: string,
) => async (dispatch: AppDispatch) => {
  const response: { data?: GoogleSheetTokensResponse; err?: string; } = {}
  dispatch(appActions.setLoading(true))
  const tokensFromAuthCodeResp = await CredentialsAPI.getGoogleTokensFromAuthCode(token, authCode)
  if (tokensFromAuthCodeResp.data) {
    const tokenVerifyResp = await GeneralAPI.verifyGoogleSheetToken(tokensFromAuthCodeResp.data.accessToken)
    if (tokenVerifyResp.data?.scope?.includes(GOOGLE_AUTH_SCOPE_DRIVE)) {
      response.data = tokensFromAuthCodeResp.data
    } else {
      response.err = tokenVerifyResp.error
      showNotification(
        { data: undefined, err: tokenVerifyResp.error },
        CredentialMessage.INVALID_GS_TOKEN,
        formattedCredentialMessage
      )
    }
  }
  dispatch(appActions.setLoading(false))
  return response
}

export const getCredentialsByUseCase = (
  token: string,
  appId: string,
  action: string,
  subject: string
) => async (dispatch: AppDispatch) => {
  dispatch(appActions.setLoading(true))
  const resp = await CredentialsAPI.getByUseCase(
    token,
    appId,
    action,
    subject
  )
  dispatch(appActions.setLoading(false))
  return resp
}
